// biome-ignore-all lint/a11y/useSemanticElements: virtualised rows are positioned divs; lists of buttons keep their roles
import { CaretRight, FlagBanner, Plus, WarningCircle } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import type { ItemLite, MilestoneView } from "@plannello/shared";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  type DragEvent,
  type KeyboardEvent,
  memo,
  type PointerEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ItemKey } from "@/components/domain/ItemKey";
import { StatusIcon } from "@/components/domain/StatusIcon";
import { TypeIcon } from "@/components/domain/TypeIcon";
import type { TimelineZoom } from "@/components/views/viewState";
import type { Catalog } from "@/data/catalog";
import { TranslatedMark, titleTooltip, useDisplayTitle, useTitles } from "@/data/titles";
import { cx } from "@/lib/cx";
import { loc } from "@/lib/format";
import { useCssPx } from "@/lib/useCssPx";
import { milestoneLabel } from "../milestoneOrder";
import {
  DAY_WIDTH,
  DEFAULT_SPAN_DAYS,
  dayOf,
  formatDay,
  formatSpan,
  headerTicks,
  itemSpan,
  KEY_STEP,
  type Span,
  type TimelineRow,
  todayDay,
  weekday,
} from "./model";

export const UNSCHEDULED_DRAG_TYPE = "application/x-plannello-unscheduled";
/** Days of the past shown left of today when the timeline opens. */
export const LEAD_DAYS: Record<TimelineZoom, number> = { weeks: 7, months: 30, quarters: 90 };
const HEADER_H = 52;
/** Month labels: side padding and the distance over which a label fades before the column. */
const MONTH_PAD = 6;
const MONTH_FADE = 28;

export type DateDraft = { start: number | null; end: number | null };

export interface TimelineCanvasProps {
  rows: TimelineRow[];
  range: { start: number; end: number };
  zoom: TimelineZoom;
  catalog: Catalog;
  /** Milestones drawn as vertical markers (those with a target date). */
  markers: MilestoneView[];
  label: string;
  peekKey: string | null;
  /** Rows can be expanded / collapsed (epics with children, milestone sections). */
  onToggle: (rowId: string) => void;
  /** Writes dates (null clears). Called on drop, after a keyboard burst, from the popover. */
  onDates: (item: ItemLite, start: string | null, due: string | null, how: "drag" | "key") => void;
  onSetDates: (item: ItemLite, anchor: HTMLElement) => void;
  onSetMilestoneDate?: (milestone: MilestoneView, anchor: HTMLElement) => void;
  onOpen: (item: ItemLite) => void;
  onPeek: (item: ItemLite) => void;
  onMilestone: (milestone: MilestoneView) => void;
  onAnnounce: (text: string) => void;
  /** Resolves an item dropped from the Unscheduled panel. */
  lookup: (id: string) => ItemLite | undefined;
  /** Ref the view uses to scroll to today. */
  scrollRef: React.RefObject<HTMLDivElement | null>;
}

const iso = (day: number) => new Date(day * 86_400_000).toISOString().slice(0, 10);

function statusTone(catalog: Catalog, item: ItemLite): "done" | "started" | "todo" | "canceled" {
  const c = catalog.statusById.get(item.statusId)?.category;
  if (c === "completed") return "done";
  if (c === "canceled") return "canceled";
  if (c === "started") return "started";
  return "todo";
}

const TONE_VAR = {
  done: "var(--status-completed)",
  started: "var(--status-started)",
  todo: "var(--fg-subtle)",
  canceled: "var(--status-canceled)",
} as const;

export type BarTone = keyof typeof TONE_VAR;
export type BarKind = "bar" | "rollup" | "anchor";

/**
 * Bar colours: the status category tints the fill (done green, started amber, to do neutral) and
 * the border; rollups (dates from children) and milestone anchors are dashed outlines without a
 * fill. Colour is never the only signal: the bar's accessible name carries status and dates.
 */
export function barColors(tone: BarTone, kind: BarKind) {
  const color = TONE_VAR[tone];
  return {
    borderColor: `color-mix(in oklab, ${color} ${kind === "bar" ? 70 : 55}%, transparent)`,
    backgroundColor:
      kind === "bar" ? `color-mix(in oklab, ${color} ${tone === "todo" ? 12 : 22}%, var(--canvas))` : "transparent",
  };
}

/**
 * The timeline itself: a sticky two-tier date header, a sticky label column and virtualised rows,
 * with today and milestone markers. Bars are buttons with roving focus: Up/Down move between rows,
 * Alt+Left/Right shift a bar by one step (a day when days are visible, else a week), Alt+Shift+
 * Left/Right change its due date, Enter opens "Set dates", Space previews, Shift+Enter opens.
 * Pointer: drag a bar to move it, its edges to resize; drop unscheduled items to schedule them.
 */
export function TimelineCanvas({
  rows,
  range,
  zoom,
  catalog,
  markers,
  label,
  peekKey,
  onToggle,
  onDates,
  onSetDates,
  onSetMilestoneDate,
  onOpen,
  onPeek,
  onMilestone,
  onAnnounce,
  lookup,
  scrollRef,
}: TimelineCanvasProps) {
  const rowH = useCssPx("--row-h", 32) + 4;
  const dayW = DAY_WIDTH[zoom];
  const step = KEY_STEP[zoom];
  const totalDays = range.end - range.start + 1;
  const width = Math.ceil(totalDays * dayW);
  const [labelW, setLabelW] = useState(280);
  const [drafts, setDrafts] = useState<Map<string, DateDraft>>(new Map());
  const [focusId, setFocusId] = useState<string | null>(null);
  const [dropX, setDropX] = useState<number | null>(null);
  const wantFocus = useRef(false);
  const gridRef = useRef<HTMLDivElement>(null);
  const today = todayDay();

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() =>
      setLabelW(el.clientWidth >= 1100 ? 340 : el.clientWidth >= 820 ? 300 : el.clientWidth >= 600 ? 240 : 180),
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, [scrollRef]);

  const xOf = useCallback((day: number) => (day - range.start) * dayW, [range.start, dayW]);

  // Scroll position: open on today; a zoom change keeps the day at the left edge; a range that
  // grows to the left (a bar dragged earlier) shifts the scroll so nothing jumps.
  const leftDay = useRef<number | null>(null);
  const prev = useRef<{ start: number; zoom: TimelineZoom } | null>(null);
  useLayoutEffect(() => {
    const sc = scrollRef.current;
    if (!sc) return;
    const p = prev.current;
    if (!p) sc.scrollLeft = Math.max(0, (today - LEAD_DAYS[zoom] - range.start) * dayW);
    else if (p.zoom !== zoom)
      sc.scrollLeft = Math.max(0, ((leftDay.current ?? today - LEAD_DAYS[zoom]) - range.start) * dayW);
    else if (p.start !== range.start) sc.scrollLeft += (p.start - range.start) * dayW;
    prev.current = { start: range.start, zoom };
    leftDay.current = range.start + sc.scrollLeft / dayW;
  }, [range.start, zoom, dayW, today, scrollRef]);
  // Only remembered for zoom changes: scrolling never re-renders the canvas.
  const onScroll = () => {
    const sc = scrollRef.current;
    if (sc) leftDay.current = range.start + sc.scrollLeft / dayW;
    scheduleMonthLabels();
  };
  // Labels stick to the visible left edge (just right of the sticky label column).
  const stickyLeft = labelW + 8;

  // Month labels in the header: positioned from the scroller's scrollLeft with a transform (no
  // layout), at most once per frame. A label pins just right of the sticky label column while its
  // month is on screen; when the month's end pushes it toward the column it fades out before it
  // can slide underneath, and the next month's label (at its own start) takes over.
  const monthEls = useRef(new Map<number, HTMLElement>());
  const monthMeasure = useRef(new Map<number, number>());
  const monthTicks = useRef<Array<{ day: number; end: number }>>([]);
  const monthFrame = useRef(0);
  const layoutMonthLabels = useCallback(() => {
    monthFrame.current = 0;
    const sc = scrollRef.current;
    if (!sc) return;
    // The track starts labelW right of the content origin and the sticky column covers the first
    // labelW pixels of the viewport, so the first visible track x is exactly scrollLeft.
    const left = sc.scrollLeft;
    const viewRight = left + sc.clientWidth - labelW;
    for (const t of monthTicks.current) {
      const el = monthEls.current.get(t.day);
      if (!el) continue;
      const start = (t.day - range.start) * dayW;
      const end = (t.end - range.start) * dayW;
      const w = monthMeasure.current.get(t.day) ?? el.offsetWidth;
      monthMeasure.current.set(t.day, w);
      let x = start + MONTH_PAD;
      let opacity = 1;
      if (end < left || start > viewRight) opacity = 0;
      else if (end - start < w + MONTH_PAD * 2)
        opacity = 0; // a month too short for its name: the grid line alone
      else {
        const room = end - MONTH_PAD - w; // right-most x the label may take
        const pinned = Math.max(start, left) + MONTH_PAD;
        x = Math.min(pinned, room);
        // Fully faded by the time the label would touch the column.
        opacity = Math.max(0, Math.min(1, (room - pinned + MONTH_PAD) / MONTH_FADE));
      }
      el.style.transform = `translate3d(${x.toFixed(1)}px,0,0)`;
      el.style.opacity = opacity < 0.01 ? "0" : String(Math.round(opacity * 100) / 100);
    }
  }, [scrollRef, labelW, range.start, dayW]);
  const scheduleMonthLabels = () => {
    if (!monthFrame.current) monthFrame.current = requestAnimationFrame(layoutMonthLabels);
  };
  // After every render that can move labels: measure and place them before paint.
  // biome-ignore lint/correctness/useExhaustiveDependencies: range.end and zoom change the month ticks the callback reads from a ref
  useLayoutEffect(() => {
    monthMeasure.current.clear();
    layoutMonthLabels();
    let live = true;
    document.fonts?.ready.then(() => {
      if (!live) return;
      monthMeasure.current.clear();
      layoutMonthLabels();
    });
    return () => {
      live = false;
    };
  }, [layoutMonthLabels, range.end, zoom]);
  useEffect(
    () => () => {
      if (monthFrame.current) cancelAnimationFrame(monthFrame.current);
    },
    [],
  );

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowH,
    overscan: 10,
    paddingEnd: 48,
    getItemKey: (i) => rows[i]!.id,
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when density changes the row height
  useLayoutEffect(() => {
    virtualizer.measure();
  }, [rowH]);

  const indexById = useMemo(() => new Map(rows.map((r, i) => [r.id, i])), [rows]);
  const rovingId = focusId && indexById.has(focusId) ? focusId : (rows[0]?.id ?? null);

  // Keyboard bursts: drafts are committed together after a short pause (one write, one undo).
  const commitTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  useEffect(() => {
    const timers = commitTimers.current;
    return () => {
      for (const t of timers.values()) clearTimeout(t);
    };
  }, []);

  const draftOf = (r: TimelineRow): Span | null => {
    if (r.kind !== "item") return null;
    // The ref, not the state: key repeats arrive before React re-renders.
    const d = draftsRef.current.get(r.id);
    if (d && d.start !== null && d.end !== null) return { start: d.start, end: d.end };
    return itemSpan(r.item);
  };

  const draftsRef = useRef(drafts);
  const setDraft = (id: string, d: DateDraft | null) => {
    const next = new Map(draftsRef.current);
    if (d) next.set(id, d);
    else next.delete(id);
    draftsRef.current = next;
    setDrafts(next);
  };

  const commit = (item: ItemLite, span: Span, how: "drag" | "key") => {
    onDates(item, iso(span.start), iso(span.end), how);
    // Keep the draft until the cache shows the new dates (no flicker back), then drop it.
    setTimeout(() => setDraft(item.id, null), 400);
  };

  const describe = (item: ItemLite, span: Span | null) =>
    span ? `${item.key}: ${formatSpan(span.start, span.end, m.tl_to())}` : `${item.key}: ${m.tl_no_dates()}`;

  const nudge = (r: Extract<TimelineRow, { kind: "item" }>, deltaStart: number, deltaEnd: number) => {
    const base = draftOf(r) ?? (r.anchor !== null ? { start: r.anchor - 6, end: r.anchor } : null);
    if (!base) return false;
    const start = base.start + deltaStart;
    const end = Math.max(start, base.end + deltaEnd);
    const span = { start, end };
    setDraft(r.id, span);
    onAnnounce(describe(r.item, span));
    const timers = commitTimers.current;
    const prev = timers.get(r.id);
    if (prev) clearTimeout(prev);
    timers.set(
      r.id,
      setTimeout(() => {
        timers.delete(r.id);
        commit(r.item, span, "key");
      }, 700),
    );
    return true;
  };

  const focusRow = (index: number) => {
    const r = rows[Math.max(0, Math.min(rows.length - 1, index))];
    if (!r) return;
    wantFocus.current = true;
    setFocusId(r.id);
    virtualizer.scrollToIndex(indexById.get(r.id) ?? 0, { align: "auto" });
  };

  useLayoutEffect(() => {
    if (!wantFocus.current || !rovingId) return;
    const el = gridRef.current?.querySelector<HTMLElement>(`[data-row-focus="${CSS.escape(rovingId)}"]`);
    if (el) {
      wantFocus.current = false;
      el.focus({ preventScroll: true });
      revealX(el);
    }
  });

  /** Scrolls horizontally so the focused bar is visible next to the sticky label column. */
  const revealX = (el: HTMLElement) => {
    const sc = scrollRef.current;
    if (!sc) return;
    const left = Number(el.dataset.left ?? 0);
    const w = el.offsetWidth;
    const viewLeft = sc.scrollLeft;
    const viewW = sc.clientWidth - labelW;
    if (left < viewLeft + 16) sc.scrollLeft = Math.max(0, left - 48);
    else if (left + w > viewLeft + viewW - 16) sc.scrollLeft = left + w - viewW + 48;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const host = (e.target as HTMLElement).closest<HTMLElement>("[data-row-focus]");
    if (!host) return;
    const id = host.dataset.rowFocus!;
    const index = indexById.get(id) ?? -1;
    const r = rows[index];
    if (!r) return;
    let handled = true;
    const k = e.key;
    if (e.altKey && (k === "ArrowLeft" || k === "ArrowRight") && r.kind === "item") {
      const d = k === "ArrowLeft" ? -step : step;
      handled = e.shiftKey ? nudge(r, 0, d) : nudge(r, d, d);
    } else if (e.altKey) handled = false;
    else if (k === "ArrowDown" || ((k === "j" || k === "J") && !e.shiftKey)) focusRow(index + 1);
    else if (k === "ArrowUp" || ((k === "k" || k === "K") && !e.shiftKey)) focusRow(index - 1);
    else if (k === "Home") focusRow(0);
    else if (k === "End") focusRow(rows.length - 1);
    else if (k === "ArrowLeft" || k === "ArrowRight") {
      const expandable = r.kind === "section" || r.childCount > 0;
      const expanded = r.kind === "section" ? !r.collapsed : r.expanded;
      if (expandable && expanded === (k === "ArrowLeft")) onToggle(r.id);
      else handled = false;
    } else if (k === "Enter" && r.kind === "item") {
      if (e.shiftKey) onOpen(r.item);
      else onSetDates(r.item, host);
    } else if (k === "Enter" && r.kind === "section") {
      if (r.milestone && r.milestone.status === "open" && onSetMilestoneDate) onSetMilestoneDate(r.milestone, host);
      else onToggle(r.id);
    } else if (k === " " && r.kind === "item") onPeek(r.item);
    else handled = false;
    if (handled) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  /* ------------------------------------------------------------ pointer drag */

  const drag = useRef<{
    row: Extract<TimelineRow, { kind: "item" }>;
    mode: "move" | "start" | "end";
    x0: number;
    base: Span;
    moved: boolean;
    pointerId: number;
  } | null>(null);

  const onBarPointerDown = (e: PointerEvent<HTMLElement>, r: Extract<TimelineRow, { kind: "item" }>, base: Span) => {
    if (e.button !== 0) return;
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const off = e.clientX - rect.left;
    const edge = rect.width >= 18 ? 7 : 0;
    const mode = off < edge ? "start" : off > rect.width - edge ? "end" : "move";
    drag.current = { row: r, mode, x0: e.clientX, base, moved: false, pointerId: e.pointerId };
    el.setPointerCapture(e.pointerId);
  };

  const onBarPointerMove = (e: PointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const dx = e.clientX - d.x0;
    if (!d.moved && Math.abs(dx) < 4) return;
    d.moved = true;
    const days = Math.round(dx / dayW);
    let { start, end } = d.base;
    if (d.mode === "move") {
      start += days;
      end += days;
    } else if (d.mode === "start") start = Math.min(end, start + days);
    else end = Math.max(start, end + days);
    const cur = drafts.get(d.row.id);
    if (!cur || cur.start !== start || cur.end !== end) setDraft(d.row.id, { start, end });
  };

  const onBarPointerUp = (e: PointerEvent<HTMLElement>) => {
    const d = drag.current;
    drag.current = null;
    if (!d || d.pointerId !== e.pointerId) return;
    if (!d.moved) {
      setFocusId(d.row.id);
      onPeek(d.row.item);
      return;
    }
    const span = drafts.get(d.row.id);
    if (span && span.start !== null && span.end !== null) {
      const s = { start: span.start, end: span.end };
      if (s.start === d.base.start && s.end === d.base.end) setDraft(d.row.id, null);
      else {
        commit(d.row.item, s, "drag");
        onAnnounce(describe(d.row.item, s));
      }
    }
  };

  // Rows are memoised: every handler they get is stable and reads the latest props through a ref,
  // and spans are cached per item object, so scrolling only renders rows that enter the window.
  const latest = useRef({
    onToggle,
    onOpen,
    onSetDates,
    onSetMilestoneDate,
    onMilestone,
    onBarPointerDown,
    onBarPointerMove,
    onBarPointerUp,
  });
  latest.current = {
    onToggle,
    onOpen,
    onSetDates,
    onSetMilestoneDate,
    onMilestone,
    onBarPointerDown,
    onBarPointerMove,
    onBarPointerUp,
  };
  const act = useMemo(
    () => ({
      toggle: (id: string) => latest.current.onToggle(id),
      open: (item: ItemLite) => latest.current.onOpen(item),
      milestone: (ms: MilestoneView) => latest.current.onMilestone(ms),
      setDates: (item: ItemLite, anchor: HTMLElement) => latest.current.onSetDates(item, anchor),
      setTarget: (ms: MilestoneView, anchor: HTMLElement) => latest.current.onSetMilestoneDate?.(ms, anchor),
      down: (e: PointerEvent<HTMLElement>, r: Extract<TimelineRow, { kind: "item" }>, base: Span) =>
        latest.current.onBarPointerDown(e, r, base),
      move: (e: PointerEvent<HTMLElement>) => latest.current.onBarPointerMove(e),
      up: (e: PointerEvent<HTMLElement>) => latest.current.onBarPointerUp(e),
    }),
    [],
  );
  const spanCache = useMemo(() => new WeakMap<ItemLite, Span | null>(), []);
  const draftSpans = useMemo(() => {
    const out = new Map<string, Span>();
    for (const [id, d] of drafts) if (d.start !== null && d.end !== null) out.set(id, { start: d.start, end: d.end });
    return out;
  }, [drafts]);
  const spanFor = (r: Extract<TimelineRow, { kind: "item" }>): Span | null => {
    const d = draftSpans.get(r.id);
    if (d) return d;
    if (!spanCache.has(r.item)) spanCache.set(r.item, itemSpan(r.item));
    return spanCache.get(r.item) ?? null;
  };

  /* ------------------------------------------------------------ drop from Unscheduled */

  const dayAtClientX = (clientX: number) => {
    const sc = scrollRef.current;
    if (!sc) return null;
    const rect = sc.getBoundingClientRect();
    const x = clientX - rect.left + sc.scrollLeft - labelW;
    if (x < 0) return null;
    return range.start + Math.floor(x / dayW);
  };
  const onDragOver = (e: DragEvent) => {
    if (!e.dataTransfer.types.includes(UNSCHEDULED_DRAG_TYPE)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const day = dayAtClientX(e.clientX);
    setDropX(day === null ? null : xOf(day));
  };
  const onDrop = (e: DragEvent) => {
    const id = e.dataTransfer.getData(UNSCHEDULED_DRAG_TYPE);
    setDropX(null);
    const day = dayAtClientX(e.clientX);
    if (!id || day === null) return;
    e.preventDefault();
    const item = lookup(id);
    if (item) onDates(item, iso(day), iso(day + DEFAULT_SPAN_DAYS - 1), "drag");
  };

  /* ------------------------------------------------------------ header */

  const ticks = useMemo(() => headerTicks(range.start, range.end, zoom), [range.start, range.end, zoom]);
  monthTicks.current = ticks.top.map((t, i) => ({ day: t.day, end: ticks.top[i + 1]?.day ?? range.end + 1 }));
  const weekendCols = useMemo(() => {
    if (zoom !== "weeks") return [];
    const out: number[] = [];
    for (let d = range.start; d <= range.end; d++) if (weekday(d) === 5) out.push(d);
    return out;
  }, [range.start, range.end, zoom]);
  const gridLines =
    zoom === "weeks" ? ticks.bottom.filter((t) => weekday(t.day) === 0) : zoom === "months" ? ticks.top : ticks.bottom;

  const items = virtualizer.getVirtualItems();
  const totalH = virtualizer.getTotalSize();

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: drop target for items dragged from the Unscheduled panel (keyboard path: Set dates)
    <div
      ref={scrollRef}
      className="scroll-thin relative min-h-0 flex-1 overflow-auto overscroll-contain"
      onScroll={onScroll}
      onDragOver={onDragOver}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) setDropX(null);
      }}
      onDrop={onDrop}
    >
      <div style={{ width: labelW + width }} className="relative min-h-full">
        {/* Header: corner + two tiers of ticks. */}
        <div className="sticky top-0 z-30 flex border-b border-line bg-canvas" style={{ height: HEADER_H }}>
          <div
            className="sticky left-0 z-10 flex shrink-0 items-end border-r border-line bg-canvas px-3 pb-1.5 text-xs font-medium text-fg-subtle"
            style={{ width: labelW }}
          >
            {label}
          </div>
          <div className="relative shrink-0" style={{ width }} aria-hidden="true">
            {ticks.top.map((t, i) => {
              const next = ticks.top[i + 1]?.day ?? range.end + 1;
              return (
                <div
                  key={`t${t.day}`}
                  className="absolute top-0 h-6 border-l border-line"
                  style={{ left: xOf(t.day), width: xOf(next) - xOf(t.day) }}
                />
              );
            })}

            {ticks.top.map((t) => (
              <span
                key={`l${t.day}`}
                ref={(el) => {
                  if (el) monthEls.current.set(t.day, el);
                  else monthEls.current.delete(t.day);
                }}
                className="pointer-events-none absolute top-0 left-0 flex h-6 items-center text-xs font-medium whitespace-nowrap text-fg will-change-transform"
                style={{ opacity: 0 }}
              >
                {t.label}
              </span>
            ))}

            {today >= range.start && today <= range.end ? (
              <div
                className="absolute bottom-0 z-[2] h-1 w-0.5 -translate-x-1/2 bg-accent"
                style={{ left: xOf(today) + dayW / 2 }}
              />
            ) : null}
            {ticks.bottom.map((t) => (
              <div
                key={`b${t.day}`}
                className={cx(
                  "absolute bottom-0 flex h-6 items-center text-2xs whitespace-nowrap tnum",
                  zoom === "weeks" ? "justify-center" : "border-l border-line pl-1",
                  t.day === today ? "font-semibold text-accent-text" : "text-fg-subtle",
                )}
                style={{ left: xOf(t.day), width: zoom === "weeks" ? dayW : undefined }}
              >
                {t.label}
              </div>
            ))}
            {markers.map((ms) => {
              const d = dayOf(ms.targetDate!);
              if (d < range.start || d > range.end) return null;
              return (
                <div
                  key={ms.id}
                  className="absolute top-0.5 z-10 flex max-w-40 -translate-x-1/2 items-center gap-1 rounded-mark border border-line bg-raised px-1 text-2xs font-medium whitespace-nowrap text-fg-muted shadow-[0_1px_2px_var(--shadow-soft)]"
                  style={{ left: xOf(d) + dayW / 2 }}
                  title={`${milestoneLabel(ms)}: ${formatDay(d)}`}
                >
                  <FlagBanner size={10} weight="fill" aria-hidden="true" className="shrink-0" />
                  <span className="truncate-title">{ms.version ?? ms.name}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Rows. */}
        <div
          ref={gridRef}
          role="list"
          aria-label={label}
          aria-describedby="tl-keys"
          onKeyDown={onKeyDown}
          className="relative"
          style={{ height: totalH }}
        >
          {/* Background: weekends, grid lines, milestone markers, today. */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-y-0" style={{ left: labelW, width }}>
            {weekendCols.map((d) => (
              <div key={d} className="absolute inset-y-0 bg-subtle" style={{ left: xOf(d), width: dayW * 2 }} />
            ))}
            {gridLines.map((t) => (
              <div key={t.day} className="absolute inset-y-0 border-l border-line" style={{ left: xOf(t.day) }} />
            ))}
            {markers.map((ms) => {
              const d = dayOf(ms.targetDate!);
              return (
                <div
                  key={ms.id}
                  className="absolute inset-y-0 border-l border-dashed border-line-strong opacity-70"
                  style={{ left: xOf(d) + dayW / 2 }}
                />
              );
            })}
            {today >= range.start && today <= range.end ? (
              <div
                className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-accent"
                style={{ left: xOf(today) + dayW / 2 }}
              />
            ) : null}
            {dropX !== null ? (
              <div className="absolute inset-y-0 w-0.5 bg-accent opacity-70" style={{ left: dropX }} />
            ) : null}
          </div>

          {items.map((v) => {
            const r = rows[v.index]!;
            return (
              <div
                key={v.key}
                role="listitem"
                className="group/row absolute inset-x-0 top-0 flex border-b border-line"
                style={{ transform: `translateY(${v.start}px)`, height: rowH }}
              >
                <RowLabel
                  row={r}
                  width={labelW}
                  catalog={catalog}
                  onToggle={act.toggle}
                  onMilestone={act.milestone}
                  onOpen={act.open}
                />
                <div className="relative shrink-0" style={{ width }}>
                  {r.kind === "section" ? (
                    <SectionTrack
                      row={r}
                      xOf={xOf}
                      dayW={dayW}
                      stickyLeft={stickyLeft}
                      tabIndex={r.id === rovingId ? 0 : -1}
                      onFocusRow={setFocusId}
                      onSetTarget={
                        r.milestone && r.milestone.status === "open" && onSetMilestoneDate ? act.setTarget : undefined
                      }
                    />
                  ) : (
                    <ItemTrack
                      row={r}
                      span={spanFor(r)}
                      drafting={drafts.has(r.id)}
                      xOf={xOf}
                      dayW={dayW}
                      today={today}
                      stickyLeft={stickyLeft}
                      catalog={catalog}
                      isPeeked={peekKey === r.item.key}
                      tabIndex={r.id === rovingId ? 0 : -1}
                      onFocusRow={setFocusId}
                      onPointerDown={act.down}
                      onPointerMove={act.move}
                      onPointerUp={act.up}
                      onOpen={act.open}
                      onSetDates={act.setDates}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <p id="tl-keys" className="sr-only">
        {m.tl_keys_help()}
      </p>
    </div>
  );
}

const RowLabel = memo(function RowLabel({
  row,
  width,
  catalog,
  onToggle,
  onMilestone,
  onOpen,
}: {
  row: TimelineRow;
  width: number;
  catalog: Catalog;
  onToggle: (id: string) => void;
  onMilestone: (ms: MilestoneView) => void;
  onOpen: (item: ItemLite) => void;
}) {
  const titleOf = useTitles();
  if (row.kind === "section") {
    const ms = row.milestone;
    return (
      <div
        className="sticky left-0 z-20 flex shrink-0 items-center gap-1.5 border-r border-line bg-subtle pr-2 pl-1.5"
        style={{ width }}
      >
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          onClick={() => onToggle(row.id)}
          className="flex size-5 shrink-0 items-center justify-center rounded-mark text-fg-subtle hover:bg-active hover:text-fg"
        >
          <CaretRight
            size={11}
            weight="bold"
            className={cx("transition-transform duration-(--dur-fast)", !row.collapsed && "rotate-90")}
          />
        </button>
        <FlagBanner
          size={14}
          weight={ms && ms.status !== "open" ? "fill" : "regular"}
          aria-hidden="true"
          className="shrink-0 text-fg-subtle"
        />
        {ms ? (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => onMilestone(ms)}
            className="min-w-0 truncate-title text-left text-sm font-medium text-fg hover:underline"
            title={milestoneLabel(ms)}
          >
            {milestoneLabel(ms)}
          </button>
        ) : (
          <span className="min-w-0 truncate-title text-sm font-medium text-fg">{m.prop_no_milestone()}</span>
        )}
        <span className="text-xs text-fg-subtle tnum">{row.count}</span>
        <span className="flex-1" />
        {ms?.targetDate ? (
          <span className="shrink-0 text-xs text-fg-muted tnum">{formatDay(dayOf(ms.targetDate), false)}</span>
        ) : null}
      </div>
    );
  }
  const { item, depth, childCount, expanded } = row;
  const shownTitle = titleOf(item);
  const type = catalog.typeById.get(item.typeId);
  const status = catalog.statusById.get(item.statusId);
  return (
    <div
      className="group/label sticky left-0 z-20 flex shrink-0 items-center gap-1.5 border-r border-line bg-canvas pr-2"
      style={{ width, paddingLeft: 6 + depth * 16 }}
    >
      {childCount > 0 ? (
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          onClick={() => onToggle(row.id)}
          className="flex size-5 shrink-0 items-center justify-center rounded-mark text-fg-subtle hover:bg-active hover:text-fg"
        >
          <CaretRight
            size={11}
            weight="bold"
            className={cx("transition-transform duration-(--dur-fast)", expanded && "rotate-90")}
          />
        </button>
      ) : (
        <span aria-hidden="true" className="w-5 shrink-0" />
      )}
      {type ? (
        <TypeIcon name={type.icon} color={type.color} label={loc(type.name)} className="shrink-0 text-fg-subtle" />
      ) : null}
      {status ? <StatusIcon category={status.category} label={loc(status.name)} /> : null}
      <button
        type="button"
        tabIndex={-1}
        onClick={() => onOpen(item)}
        className={cx(
          "min-w-0 flex-1 truncate-title text-left text-sm text-fg hover:underline",
          childCount > 0 && "font-medium",
        )}
        title={`${item.key} ${titleTooltip(shownTitle, item.title)}`}
      >
        {shownTitle.text}
        <TranslatedMark d={shownTitle} />
      </button>
      <ItemKey value={item.key} className="@max-lg:hidden" />
    </div>
  );
});

const SectionTrack = memo(function SectionTrack({
  row,
  xOf,
  dayW,
  stickyLeft,
  tabIndex,
  onFocusRow,
  onSetTarget,
}: {
  row: Extract<TimelineRow, { kind: "section" }>;
  xOf: (d: number) => number;
  dayW: number;
  /** Scroller x where labels stick (right of the label column). */
  stickyLeft: number;
  tabIndex: number;
  onFocusRow: (id: string) => void;
  /** Open milestones the person may manage: set the target date from the track. */
  onSetTarget?: (milestone: MilestoneView, anchor: HTMLElement) => void;
}) {
  const ms = row.milestone;
  const target = ms?.targetDate ? dayOf(ms.targetDate) : null;
  const label = ms
    ? `${milestoneLabel(ms)}, ${target !== null ? `${m.ms_target()} ${formatDay(target)}` : m.tl_no_target()}, ${row.count === 1 ? m.tl_items_count_one() : m.tl_items_count({ count: row.count })}`
    : `${m.prop_no_milestone()}, ${row.count === 1 ? m.tl_items_count_one() : m.tl_items_count({ count: row.count })}`;
  const anchorX = target !== null ? xOf(target) + dayW / 2 : null;
  const spanStart = row.span ? xOf(row.span.start) : null;
  return (
    <div className="absolute inset-0 flex items-center">
      {spanStart !== null && anchorX !== null && spanStart < anchorX ? (
        <div
          aria-hidden="true"
          className="absolute top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-line-strong opacity-60"
          style={{ left: spanStart, width: anchorX - spanStart }}
        />
      ) : null}
      <button
        type="button"
        data-row-focus={row.id}
        data-left={anchorX ?? 0}
        tabIndex={tabIndex}
        onFocus={() => onFocusRow(row.id)}
        onClick={onSetTarget && ms ? (e) => onSetTarget(ms, e.currentTarget) : undefined}
        aria-label={label}
        aria-expanded={!row.collapsed}
        className={cx(
          "flex items-center outline-hidden",
          anchorX !== null ? "absolute top-1/2 -translate-y-1/2" : "sticky",
          "focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-focus",
          anchorX !== null
            ? "size-3.5 -translate-x-1/2 rotate-45 rounded-mark border border-fg-muted bg-raised"
            : onSetTarget
              ? "h-5 gap-1 rounded-control border border-dashed border-line-strong px-1.5 text-2xs whitespace-nowrap text-fg-muted hover:bg-hover hover:text-fg"
              : "h-5 w-6 rounded-control",
        )}
        style={{ left: anchorX ?? stickyLeft }}
      >
        {anchorX === null && onSetTarget ? (
          <>
            <Plus size={10} aria-hidden="true" />
            {m.ms_set_target_short()}
          </>
        ) : null}
      </button>
    </div>
  );
});

const ItemTrack = memo(function ItemTrack({
  row,
  span,
  drafting,
  xOf,
  dayW,
  today,
  stickyLeft,
  catalog,
  isPeeked,
  tabIndex,
  onFocusRow,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onOpen,
  onSetDates,
}: {
  row: Extract<TimelineRow, { kind: "item" }>;
  span: Span | null;
  drafting: boolean;
  xOf: (d: number) => number;
  dayW: number;
  today: number;
  /** Scroller x where labels stick: titles of bars that start off-screen stay readable. */
  stickyLeft: number;
  catalog: Catalog;
  isPeeked: boolean;
  tabIndex: number;
  onFocusRow: (id: string) => void;
  onPointerDown: (e: PointerEvent<HTMLElement>, r: Extract<TimelineRow, { kind: "item" }>, base: Span) => void;
  onPointerMove: (e: PointerEvent<HTMLElement>) => void;
  onPointerUp: (e: PointerEvent<HTMLElement>) => void;
  onOpen: (item: ItemLite) => void;
  onSetDates: (item: ItemLite, anchor: HTMLElement) => void;
}) {
  const { item } = row;
  const shownTitle = useDisplayTitle(item);
  const tone = statusTone(catalog, item);
  const own = itemSpan(item);
  const closed = tone === "done" || tone === "canceled";
  const overdue = !closed && !!item.dueDate && dayOf(item.dueDate) < today;
  const statusName = loc(catalog.statusById.get(item.statusId)?.name);

  // What to draw: own dates (or the draft), else a rollup from children, else an anchor ghost.
  const shown: Span | null =
    span ?? row.rollup ?? (row.anchor !== null ? { start: row.anchor - 6, end: row.anchor } : null);
  const kind: "bar" | "rollup" | "anchor" | "none" = span
    ? "bar"
    : row.rollup
      ? "rollup"
      : row.anchor !== null
        ? "anchor"
        : "none";

  if (!shown) {
    // No dates: a quiet "set dates" target at today so every row stays keyboard reachable.
    const left = xOf(today) + 6;
    return (
      <button
        type="button"
        data-row-focus={row.id}
        data-timeline-bar=""
        data-left={left}
        tabIndex={tabIndex}
        onFocus={() => onFocusRow(row.id)}
        onClick={(e) => onSetDates(item, e.currentTarget)}
        aria-label={`${item.key} ${shownTitle.text}, ${statusName}, ${m.tl_no_dates()}`}
        className={cx(
          "absolute top-1/2 flex h-5 -translate-y-1/2 items-center gap-1 rounded-control border border-dashed border-line-strong px-1.5 text-2xs whitespace-nowrap text-fg-subtle outline-hidden transition-ui",
          "opacity-0 group-hover/row:opacity-100 hover:bg-hover hover:text-fg focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-1 focus-visible:outline-focus",
          tabIndex === 0 && "opacity-100",
        )}
        style={{ left }}
      >
        <Plus size={10} aria-hidden="true" />
        {m.tl_set_dates()}
      </button>
    );
  }

  const left = xOf(shown.start);
  const width = Math.max(dayW * (shown.end - shown.start + 1), 8);
  const showInside = width >= 96;
  const text = `${item.key} ${titleTooltip(shownTitle, shownTitle.text)}`;
  const spanText = formatSpan(shown.start, shown.end, m.tl_to());
  const aria = [
    `${item.key} ${shownTitle.text}`,
    statusName,
    kind === "bar"
      ? spanText
      : kind === "rollup"
        ? m.tl_rollup_label({ span: spanText })
        : m.tl_anchor_label({ date: formatDay(row.anchor!) }),
    overdue ? m.date_overdue() : null,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <>
      <button
        type="button"
        data-row-focus={row.id}
        data-timeline-bar=""
        data-left={left}
        tabIndex={tabIndex}
        onFocus={() => onFocusRow(row.id)}
        aria-label={aria}
        aria-roledescription={m.tl_bar_role()}
        onPointerDown={kind === "rollup" ? undefined : (e) => onPointerDown(e, row, shown)}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={() => onOpen(item)}
        onClick={kind === "rollup" ? (e) => onSetDates(item, e.currentTarget) : undefined}
        className={cx(
          "absolute top-1/2 flex h-[calc(100%-12px)] max-h-6 min-h-4 -translate-y-1/2 touch-none items-center overflow-clip rounded-control border text-left text-xs outline-hidden select-none",
          "focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-focus",
          kind === "bar" ? "cursor-grab active:cursor-grabbing" : "cursor-pointer",
          kind !== "bar" && "border-dashed",
          drafting && "shadow-(--elev-md)",
          isPeeked && "outline-2 outline-solid outline-offset-1 outline-accent",
          row.item.version === 0 && "opacity-60",
        )}
        style={{ left, width, ...barColors(tone, kind as BarKind) }}
      >
        {kind === "bar" && own?.open !== "start" ? (
          <span aria-hidden="true" className="absolute inset-y-0 left-0 w-1.5 cursor-ew-resize" />
        ) : null}
        {showInside ? (
          <span className="sticky flex min-w-0 items-center gap-1.5 px-2 text-fg" style={{ left: stickyLeft }}>
            {overdue ? (
              <WarningCircle size={12} weight="fill" aria-hidden="true" className="shrink-0 text-danger-text" />
            ) : null}
            <span className="truncate-title">{shownTitle.text}</span>
          </span>
        ) : null}
        {kind === "bar" ? (
          <span aria-hidden="true" className="absolute inset-y-0 right-0 w-1.5 cursor-ew-resize" />
        ) : null}
      </button>
      {showInside ? null : (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 flex max-w-72 -translate-y-1/2 items-center gap-1 truncate-title text-xs whitespace-nowrap text-fg-muted"
          style={{ left: left + width + 6 }}
          title={text}
        >
          {overdue ? <WarningCircle size={12} weight="fill" className="shrink-0 text-danger-text" /> : null}
          {shownTitle.text}
        </span>
      )}
    </>
  );
});
